import { Check } from "lucide-react";
import type { ReactNode } from "react";
import { Card } from "@/components/ui/card";
import type { OnboardingStepMeta } from "@/features/onboarding/steps-meta";
import { cn } from "@/lib/utils";

type StepState = "done" | "current" | "skipped" | "pending";

interface StepListProps {
  steps: OnboardingStepMeta[];
  current: number;
  done: boolean[];
  skipped: number[];
  doneCaptions: (string | null)[];
  onSelect: (index: number) => void;
  /** Rendered inside the panel below the steps (e.g. the unlock card). */
  footer?: ReactNode;
}

function stateFor(
  i: number,
  current: number,
  done: boolean[],
  skipped: number[],
): StepState {
  if (i === current) return "current";
  if (done[i]) return "done";
  if (skipped.includes(i)) return "skipped";
  return "pending";
}

/** The left rail: all six steps at once with done / current / skipped / pending
 * treatments. Clicking any row activates its detail panel. */
export function StepList({
  steps,
  current,
  done,
  skipped,
  doneCaptions,
  onSelect,
  footer,
}: StepListProps) {
  return (
    <Card className="gap-0.5 p-2.5">
      {steps.map((step, i) => {
        const state = stateFor(i, current, done, skipped);
        return (
          <button
            key={step.key}
            type="button"
            onClick={() => onSelect(i)}
            aria-current={state === "current" ? "step" : undefined}
            className={cn(
              "flex items-center gap-3 rounded-md px-3 py-3 text-left transition-colors",
              state === "current"
                ? "bg-warning-soft border-warning/40 border"
                : "hover:bg-muted/60",
            )}
          >
            <StepBadge state={state} index={i} />
            <span className="flex min-w-0 flex-grow flex-col leading-tight">
              <span
                className={cn(
                  "text-sm font-semibold",
                  state === "done" ? "text-muted-foreground line-through" : "text-foreground",
                )}
              >
                {step.listTitle}
              </span>
              <StepCaption state={state} step={step} caption={doneCaptions[i] ?? null} />
            </span>
            {state === "done" && (
              <span className="text-muted-foreground text-xs">Edit</span>
            )}
            {state === "skipped" && (
              <span className="text-info text-xs font-semibold">Resume</span>
            )}
          </button>
        );
      })}
      {footer}
    </Card>
  );
}

function StepBadge({ state, index }: { state: StepState; index: number }) {
  if (state === "done") {
    return (
      <span className="bg-success-soft text-success flex size-[34px] shrink-0 items-center justify-center rounded-full">
        <Check className="size-4" strokeWidth={3} />
      </span>
    );
  }
  if (state === "current") {
    return (
      <span className="bg-primary text-primary-foreground font-heading flex size-[34px] shrink-0 items-center justify-center rounded-full text-sm font-bold shadow-[0_6px_14px_-6px_var(--primary)]">
        {index + 1}
      </span>
    );
  }
  if (state === "skipped") {
    return (
      <span className="border-warning text-warning font-heading flex size-[34px] shrink-0 items-center justify-center rounded-full border-2 border-dashed text-[13px] font-bold">
        {index + 1}
      </span>
    );
  }
  return (
    <span className="bg-muted text-muted-foreground font-heading flex size-[34px] shrink-0 items-center justify-center rounded-full text-[13px] font-bold">
      {index + 1}
    </span>
  );
}

function StepCaption({
  state,
  step,
  caption,
}: {
  state: StepState;
  step: OnboardingStepMeta;
  caption: string | null;
}) {
  if (state === "done") {
    return <span className="text-success text-xs">{caption ?? "Done"}</span>;
  }
  if (state === "current") {
    return (
      <span className="text-warning text-xs">You're here · {step.minutes} min</span>
    );
  }
  if (state === "skipped") {
    return <span className="text-warning text-xs">Skipped · we'll remind you</span>;
  }
  return (
    <span className="text-muted-foreground text-xs">
      {step.minutes} min{step.durationHint ? ` · ${step.durationHint}` : ""}
    </span>
  );
}
